import React, {PropsWithChildren, useEffect} from 'react';
import {useQuery} from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import * as utc from 'dayjs/plugin/utc';
import {API} from '@chekin/api';
import {
  ShortReservation,
  Housing,
  ORIGINS,
  QUERY_CACHE_KEYS,
  isReservationNotFoundError,
} from '@chekin/core';
import {useChekinSDK, useIsTokenReady} from '../ChekinSDK';
import {useFetchHousing} from 'hooks/useFetchHousing';
import {Analytics} from '@chekin/shared';

type ContextProps = {
  data?: ShortReservation;
  housing?: Housing;
  error?: Error | null;
  isLoading?: boolean;
  isFetching?: boolean;
  isNotFound?: boolean;
  housingId?: string;
  reservationId?: string;
  isBiometricMatchForAll?: boolean;
  isSuccessLoadedHousing?: boolean;
  hasAvantio?: boolean;
  allowEditingGuestNumber?: boolean;
};

enum GUESTAPP_V1_DOMAINS {
  staging = 'new-chekin.stg.chekin.com',
  production = 'guestapp.chekin.com',
}

const ReservationContext = React.createContext<ContextProps>({
  data: undefined,
  housing: undefined,
  isLoading: false,
  isFetching: false,
});

function ReservationProvider({children}: Readonly<PropsWithChildren>) {
  const {foundReservationId, isSuccessSDK} = useChekinSDK();
  const isTokenReady = useIsTokenReady();
  dayjs.extend(utc.default);
  dayjs.extend(isBetween);
  const enabled = isTokenReady && Boolean(foundReservationId) && isSuccessSDK;

  const {
    data: reservation,
    error,
    isLoading,
    isFetching,
    isError,
  } = useQuery<ShortReservation, Error & {status?: number}>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [QUERY_CACHE_KEYS.reservation],
    queryFn: () => API.reservation.getOne({id: foundReservationId || ''}),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled,
  });

  useEffect(() => {
    if (
      reservation &&
      (reservation?.signup_form_link.includes(GUESTAPP_V1_DOMAINS.staging) ||
        reservation?.signup_form_link.includes(GUESTAPP_V1_DOMAINS.production))
    ) {
      window.location.href = reservation?.signup_form_link;
    }
  }, [reservation]);

  useEffect(() => {
    if (!reservation?.id) {
      return;
    }
    Analytics.initialize();
    Analytics.identify(reservation.id);
    Analytics.addEventProperties({
      reservation_id: reservation.id,
      environment: import.meta.env.MODE,
      platform: 'web',
    });
  }, [reservation?.id]);

  const housingId = reservation?.housing_id;

  const isNotFound = isError && isReservationNotFoundError(error);

  useEffect(() => {
    Sentry.setTags({
      reservation_id: reservation?.id,
      housing_id: housingId,
      origin: reservation?.origin,
    });
  }, [reservation?.id, reservation?.origin, housingId]);

  const {data: housing, isSuccess: isSuccessLoadedHousing} = useFetchHousing(housingId, {
    initialData: {} as Housing,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: Boolean(housingId),
  });

  const isBiometricMatchForAll = housing?.is_biometric_match_for_all_enabled;
  const hasAvantio = reservation?.origin === ORIGINS.avantio;
  const allowEditingGuestNumber = housing?.allow_guest_number_modification;
  const defaultProps = React.useMemo<ContextProps>(
    () => ({
      data: reservation,
      reservationId: reservation?.id || '',
      housing,
      housingId,
      error,
      isLoading,
      isFetching,
      isNotFound,
      isBiometricMatchForAll,
      isSuccessLoadedHousing,
      hasAvantio,
      allowEditingGuestNumber,
    }),
    [
      error,
      housing,
      housingId,
      isBiometricMatchForAll,
      isFetching,
      isLoading,
      isNotFound,
      reservation,
      isSuccessLoadedHousing,
      hasAvantio,
      allowEditingGuestNumber,
    ],
  );

  return <ReservationContext.Provider value={defaultProps} children={children} />;
}

export {ReservationContext, ReservationProvider};
